import { useNavigate } from "@tanstack/react-router";
import { Lightbulb } from "lucide-react";
import { CompetencyCards } from "@/components/competencies/CompetencyCards";
import { CompetencyFormDialog } from "@/components/competencies/CompetencyFormDialog";
import { interactiveRowProps } from "@/components/shared/interactive-row";
import { LibraryListView, useLibraryList } from "@/components/shared/library-list";
import { LifecycleBadge } from "@/components/shared/lifecycle-badge";
import { OriginLink } from "@/components/shared/origin-link";
import { useCompetencies } from "@/hooks/use-competencies";
import { carryListSearch } from "@/lib/list-search";

export function CompetenciesTable() {
  const navigate = useNavigate();
  const list = useLibraryList("competencies.view");
  const result = useCompetencies(list.query, list.pageRequest);

  return (
    <LibraryListView
      list={list}
      result={result}
      noun={{ one: "competency", many: "competencies" }}
      icon={Lightbulb}
      headers={[
        { label: "Name", sortKey: "name" },
        { label: "Categories", className: "hidden sm:table-cell" },
        { label: "Role", sortKey: "roleName", className: "hidden sm:table-cell" },
        { label: "Status", sortKey: "status" },
      ]}
      renderCards={({ items, loading, empty }) => (
        <CompetencyCards competencies={items} loading={loading} empty={empty} />
      )}
      renderCreateDialog={({ open, onOpenChange }) => (
        <CompetencyFormDialog open={open} onOpenChange={onOpenChange} />
      )}
      renderRow={(c) => (
        <tr
          key={c.id}
          {...interactiveRowProps(
            () =>
              navigate({
                to: "/dashboard/competencies/$competencyId",
                params: { competencyId: c.id },
                search: carryListSearch(),
              }),
            `Open ${c.name}`,
          )}
        >
          <td className="px-4 py-2.5 font-medium text-foreground">{c.name}</td>
          {/* Not sortable: a competency can be in several categories, so
              there is no single value to sort on — the filter arrives with
              the competency report (WI-318). */}
          <td className="hidden sm:table-cell px-4 py-2.5 text-muted-foreground">
            {c.categories.length === 0 ? (
              "—"
            ) : (
              <span className="line-clamp-1">{c.categories.map((cat) => cat.name).join(", ")}</span>
            )}
          </td>
          <td className="hidden sm:table-cell px-4 py-2.5 text-muted-foreground">
            {c.roles.length === 0 ? (
              "—"
            ) : (
              <span className="line-clamp-1">
                {c.roles.map((r, i) => (
                  <span key={r.id}>
                    {i > 0 && ", "}
                    <OriginLink
                      to="/dashboard/roles/$roleId"
                      params={{ roleId: r.id }}
                      onClick={(e) => e.stopPropagation()}
                      className="text-primary hover:underline"
                    >
                      {r.name}
                    </OriginLink>
                  </span>
                ))}
              </span>
            )}
          </td>
          <td className="px-4 py-2.5">
            <LifecycleBadge status={c.status} />
          </td>
        </tr>
      )}
    />
  );
}
